import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { AdminService } from '../../../services/admin.service';
import {
  PAYROLL_CURRENCY_CODES,
  PAYROLL_CURRENCY_SETTING_FIELDS,
  CurrencyCode,
  PayrollCurrencySetting,
  PayrollCurrencySettingField,
  PayrollCurrencySettings,
  PayrollCurrencySettingViewField
} from '../../../models/payroll-currency.model';
import { ViewUserReportHelper } from './view-user-report.helper';

@Injectable()
export class PayrollCurrencySettingsService implements OnDestroy {
  public readonly fields = PAYROLL_CURRENCY_SETTING_FIELDS;
  public settings: PayrollCurrencySettings = null;
  public loading = false;
  public saving = false;
  public error = false;

  private readonly destroy$ = new Subject<void>();

  public constructor(private adminService: AdminService) {}

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public load(): void {
    if (this.settings || this.loading) {
      return;
    }
    this.loading = true;
    this.error = false;
    this.adminService.getPayrollCurrencySettings()
      .takeUntil(this.destroy$)
      .subscribe(
        settings => this.handleLoaded(settings),
        () => this.handleFailed()
      );
  }

  public save(onSaved: () => void): void {
    if (!this.canSave) {
      return;
    }
    this.saving = true;
    this.error = false;
    this.adminService.updatePayrollCurrencySettings(this.settings)
      .takeUntil(this.destroy$)
      .subscribe(
        settings => {
          this.handleLoaded(settings);
          this.saving = false;
          onSaved();
        },
        () => this.handleFailed()
      );
  }

  public get canSave(): boolean {
    return !!this.settings
      && !this.saving
      && this.hasEveryCurrency()
      && this.settings.currencies.every(setting => {
        return this.fields.every(field => {
          return ViewUserReportHelper.isNonNegativeFiniteNumber(setting[field.field]);
        });
      });
  }

  public getValue(
    setting: PayrollCurrencySetting,
    field: PayrollCurrencySettingField
  ): number {
    return setting[field];
  }

  public setValue(
    setting: PayrollCurrencySetting,
    field: PayrollCurrencySettingField,
    value: number
  ): void {
    setting[field] = value;
  }

  public trackByCurrency(_index: number, setting: PayrollCurrencySetting): CurrencyCode {
    return setting.currency;
  }

  public trackByField(_index: number, field: PayrollCurrencySettingViewField): string {
    return field.field;
  }

  private hasEveryCurrency(): boolean {
    const currencies = this.settings.currencies;
    return currencies.length === PAYROLL_CURRENCY_CODES.length
      && PAYROLL_CURRENCY_CODES.every(currency => {
        return currencies.some(setting => setting.currency === currency);
      });
  }

  private handleLoaded(settings: PayrollCurrencySettings): void {
    this.settings = settings;
    this.loading = false;
    this.error = false;
  }

  private handleFailed(): void {
    this.loading = false;
    this.saving = false;
    this.error = true;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-user-report/payroll-currency-settings.service.ts